import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

const initialState = {
  videos: [],
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
};

// A helper function to handle API responses
const handleApiResponse = async (response, rejectWithValue) => {
  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    const data = await response.json();
    if (!response.ok) {
      return rejectWithValue(data.detail || 'An API error occurred.');
    }
    return data;
  } else {
    // The response is not JSON, likely an HTML error page from a proxy issue
    return rejectWithValue('Failed to connect to the API. Please ensure the backend server is running and accessible.');
  }
};

// Async thunk to fetch videos from the server
export const fetchVideos = createAsyncThunk('videos/fetchVideos', async (_, { rejectWithValue, getState }) => {
  try {
    const state = getState();
    const baseURL = state.channel.backendBaseURL || 'https://api.ask-cara.com';
    const response = await fetch(`${baseURL}/api/v1/videos`);
    return await handleApiResponse(response, rejectWithValue);
  } catch (error) {
    return rejectWithValue('Network error: Could not fetch videos.');
  }
});

// Async thunk to upload a new video
export const uploadVideo = createAsyncThunk('videos/uploadVideo', async (formData, { rejectWithValue, getState }) => {
  try {
    const state = getState();
    const baseURL = state.channel.backendBaseURL || 'https://api.ask-cara.com';
    const response = await fetch(`${baseURL}/api/v1/video`, {
      method: 'POST',
      body: formData,
      // Do NOT set Content-Type header for FormData, browser does it automatically with boundary
    });
    return await handleApiResponse(response, rejectWithValue);
  } catch (error) {
    return rejectWithValue('Network error: Could not upload video.');
  }
});


const videoSlice = createSlice({
  name: 'videos',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchVideos.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchVideos.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.videos = action.payload;
      })
      .addCase(fetchVideos.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      })
      .addCase(uploadVideo.fulfilled, (state, action) => {
        state.videos.push(action.payload); // Add the new video to the list
        state.status = 'succeeded';
      })
      .addCase(uploadVideo.rejected, (state, action) => {
        state.error = action.payload || action.error.message; // Show upload error
        state.status = 'failed';
      })
  },
});

export default videoSlice.reducer;
